import type { ChecklistItem, ChecklistSummary, ChecklistStats, SortColumn, SortDirection } from '../types';

/**
 * Generate IKEA product image URL from product code
 */
export function getIkeaImageUrl(productCode: string): string {
  const cleanCode = productCode.replace(/\./g, '').replace(/\D/g, '');
  return `https://www.ikea.com/us/en/images/products/${cleanCode.slice(0, 5)}/${cleanCode}_40.jpg`;
}

/**
 * Generate IKEA product page URL from product code
 */
export function getIkeaProductUrl(productCode: string): string {
  return `https://www.ikea.com/us/en/p/-${productCode.replace(/\./g, '')}/`;
}

/**
 * Sort checklist items by column and direction
 */
export function sortItems(
  items: ChecklistItem[],
  sortColumn: SortColumn,
  sortDirection: SortDirection
): ChecklistItem[] {
  return [...items].sort((a, b) => {
    let comparison = 0;
    switch (sortColumn) {
      case 'code':
        comparison = (a.productCode || '').localeCompare(b.productCode || '');
        break;
      case 'name':
        comparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'qty':
        comparison = (a.quantityNeeded || 1) - (b.quantityNeeded || 1);
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
}

/**
 * Filter items by search query
 */
export function filterItems(items: ChecklistItem[], filter: string): ChecklistItem[] {
  if (!filter) return items;
  const lowerFilter = filter.toLowerCase();
  return items.filter(item =>
    item.name?.toLowerCase().includes(lowerFilter) ||
    item.productCode?.includes(filter) ||
    item.notes?.toLowerCase().includes(lowerFilter)
  );
}

/**
 * Calculate aggregate stats across all checklists
 */
export function calculateStats(checklists: ChecklistSummary[], allItems?: ChecklistItem[]): ChecklistStats {
  const totalChecklists = checklists.length;
  const totalItems = checklists.reduce((sum, c) => sum + c.itemCount, 0);
  const completedItems = checklists.reduce((sum, c) => sum + c.completedCount, 0);

  // Estimate total from items if available
  let estimatedTotal = 0;
  if (allItems) {
    estimatedTotal = allItems.reduce((sum, item) => {
      return sum + (item.estimatedPrice || 0) * (item.quantityNeeded || 1);
    }, 0);
  }

  return {
    totalChecklists,
    totalItems,
    completedItems,
    estimatedTotal,
  };
}

/**
 * Calculate progress percentage for a checklist
 */
export function calculateProgress(itemCount: number, completedCount: number): number {
  if (itemCount === 0) return 0;
  return Math.round((completedCount / itemCount) * 100);
}

/**
 * Get next status in the workflow cycle
 */
export function getNextStatus(currentStatus: string): 'pending' | 'purchased' | 'installed' {
  switch (currentStatus) {
    case 'pending':
      return 'purchased';
    case 'purchased':
      return 'installed';
    default:
      return 'pending';
  }
}
